import { type FC, useEffect, useRef, useState } from 'react';
import { OrganizationSankey } from '../OrganizationSankey';
import { useFetchOrganizationSankey } from 'api/organization/organizationServices';
import OrganizationSankeyButtons from './OrganizationSankeyButtons';
import { UtilityTypeEnum } from 'enums/UtilityTypeEnum';
import { CardContainer, CardTitle } from 'layout/Card';
import { useTranslation } from 'react-i18next';

const OrganizationEnergyOverview: FC = () => {
  const { t } = useTranslation();
  const [selectedUtilityType, setSelectedUtilityType] = useState(
    UtilityTypeEnum.ELECTRICITY
  );
  const { data } = useFetchOrganizationSankey({
    utilityType: selectedUtilityType,
  });

  // Track if this is the initial load to prevent auto-switching after user selections
  const hasInitialized = useRef(false);

  // Auto-select the first available utility type only on initial load
  useEffect(() => {
    if (
      data?.utility_types &&
      data.utility_types.length > 0 &&
      !hasInitialized.current
    ) {
      const firstAvailableType = data.utility_types[0].type;

      // Convert to uppercase to match UtilityTypeEnum values
      const upperCaseType = firstAvailableType.toUpperCase();

      const enumKey = Object.keys(UtilityTypeEnum).find(
        (key) =>
          UtilityTypeEnum[key as keyof typeof UtilityTypeEnum] === upperCaseType
      );

      if (enumKey) {
        const firstUtilityType =
          UtilityTypeEnum[enumKey as keyof typeof UtilityTypeEnum];

        // Only update if current selection is the default ELECTRICITY and it's different from first available
        if (
          selectedUtilityType === UtilityTypeEnum.ELECTRICITY &&
          selectedUtilityType !== firstUtilityType
        ) {
          setSelectedUtilityType(firstUtilityType);
        }
      }

      // Mark as initialized so this only runs once
      hasInitialized.current = true;
    }
  }, [data?.utility_types]);

  const handleSelection = (utilityType: string): void => {
    const enumKey = Object.keys(UtilityTypeEnum).find(
      (key) =>
        UtilityTypeEnum[key as keyof typeof UtilityTypeEnum] === utilityType
    );

    if (enumKey) {
      setSelectedUtilityType(
        UtilityTypeEnum[enumKey as keyof typeof UtilityTypeEnum]
      );
    }
  };

  return (
    <>
      <CardTitle title={t('dashboardPage.energyOverview')} fontSize="sm" />
      <CardContainer>
        <div className="my-6">
          <OrganizationSankeyButtons
            utilityTypes={data?.utility_types}
            selectedUtilityType={selectedUtilityType}
            handleSelection={handleSelection}
          />
        </div>

        <OrganizationSankey data={data?.sankey} />
      </CardContainer>
    </>
  );
};
export default OrganizationEnergyOverview;
